import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAccountStore } from '@/store/use-account-store';
import { INCIDENT_AFFECTED_ADDRESSES } from './affected-addresses';
import { Dialog, DialogContent } from '@/components/ui';

// NOTE: change version every time content changes
const VERSION = '2';
const STORAGE_KEY = 'incident-compensation-modal-dismissed';

export default function IncidentCompensationModal() {
  const address = useAccountStore((state) => state.address);
  const hasShown = useRef(false);

  const isAffected =
    !!address &&
    INCIDENT_AFFECTED_ADDRESSES.some(
      (a) => a.toLowerCase() === address.toLowerCase(),
    );

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isAffected) return;
    if (hasShown.current) return;
    if (localStorage.getItem(STORAGE_KEY) === VERSION) return;

    hasShown.current = true;
    setIsOpen(true);
  }, [isAffected]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      localStorage.setItem(STORAGE_KEY, VERSION);
    }
    setIsOpen(open);
  };

  if (!isAffected) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="gap-0 border border-border bg-background p-5 sm:max-w-2xl sm:rounded-none"
        closeClassName="right-2 top-2"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1 pr-8">
          <div className="mb-1 flex items-center gap-2 text-sm uppercase tracking-wide">
            <Info size={12} className="inline-block" /> Notice
          </div>
          <div className="space-y-3 text-vestgrey-500">
            <p>
              <strong>Dear User,</strong>
            </p>
            <p>
              You're receiving this message because your account was impacted by
              the pricing bug on 4/15. Your account has been credited for the
              losses caused by the incident. Our compensation methodology makes
              users whole for any negative PNL experienced as a result of the
              outage and subsequent rollback.
            </p>
            <p>
              If you were a Vest Capital user, you will receive either an eval
              account or a reinstated funded account alongside your uPNL
              compensation, as well as a code for an additional free Silver eval
              account.
            </p>
            <p>
              We apologize for the impact this has had and are committed to
              ensuring all users are made whole. Our team deeply appreciates
              your continued support. Please reach out to support with any
              questions.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
